// Me, the account menu (NAV-REDESIGN: the rail's foot, under Settings; the bottom bar on phones;
// the top bar where there is no rail): who is signed in; "Your settings" (SETUP-REGROUP, review #18), the
// personal pages that are not the company's Setup — Account & security (`/account`), Appearance
// (`/settings/appearance`, was Setup › Appearance), Notifications (`/c/{slug}/notifications`,
// SEARCH-NOTIF's page, per company; `/settings/notifications` redirects there) and My
// subscriptions (both inside a company); My agent (a company whose `agents` add-on is on, M4-01);
// presenting; sign out. The theme radio left the menu: it repeated Appearance (review §4.3). The
// old "Compact bar" item is gone with the mode tabs it compacted (design spec 2026-10-19 §8.5).
import {
  BottomBarMenu,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  initialsOf,
  ProfileCard,
  TopbarIconButton,
} from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import { BotIcon, CircleUserRoundIcon, LogOutIcon } from "lucide-react";
import type { MeResponse } from "../api/types";
import type { CompanyAccess } from "../features/company";
import { YOUR_SETTINGS } from "../features/settings/setup-catalog";
import { useShellState } from "./shell-state";

type MeMenuProps = {
  me: MeResponse;
  /**
   * The company in context: adds "My subscriptions" (BYO-01) for it, and "My agent" (M4-01) when
   * its `agents` add-on is on.
   */
  company?: CompanyAccess | null;
  onSignOut: () => void;
  /**
   * NAV-REDESIGN: where the menu's button sits — the top bar (a round avatar), the rail's foot
   * (the avatar over "Me") or the phone's bottom bar ("Me").
   */
  variant?: "topbar" | "rail" | "bottom";
};

// The rail item's look (packages/ui rail.tsx), as a menu button.
const RAIL_ME =
  "flex size-12 flex-col items-center justify-center gap-0.5 rounded-md text-secondary transition-colors duration-(--dur-fast) ease-out hover:bg-muted hover:text-foreground data-[popup-open]:bg-muted outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring";

export function MeMenu({ variant = "topbar", ...props }: MeMenuProps) {
  const name = props.me.user.name.trim() || props.me.user.email;
  const avatar = (size: string) => (
    <span
      className={`flex ${size} items-center justify-center rounded-full bg-primary-soft text-[10px] font-semibold text-primary-text`}
    >
      {initialsOf(name)}
    </span>
  );
  if (variant === "bottom")
    return (
      <BottomBarMenu icon={CircleUserRoundIcon} label="Me" aria-label={`Account: ${name}`}>
        <MeMenuItems {...props} />
      </BottomBarMenu>
    );
  if (variant === "rail")
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={`Account: ${name}`}
          data-slot="rail-me"
          data-mode="me"
          className={RAIL_ME}
        >
          {avatar("size-6")}
          <span className="text-[10px] leading-3 font-medium tracking-[-0.2px]">Me</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="end" className="min-w-64">
          <MeMenuItems {...props} />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<TopbarIconButton aria-label={`Account: ${name}`} className="rounded-full" />}
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary-text">
          {initialsOf(name)}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-64">
        <MeMenuItems {...props} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The menu's rows: who is signed in, Your settings, My agent, Presenting, Sign out. */
function MeMenuItems({ me, company = null, onSignOut }: Omit<MeMenuProps, "variant">) {
  const shell = useShellState();
  // A company's personal pages (Notifications, My subscriptions) need a company in context.
  const personal = YOUR_SETTINGS.filter((e) => (e.company ? company !== null : true));
  // An account made by an email link may have no name yet.
  const name = me.user.name.trim() || me.user.email;
  // A crew is a person's (an agency operator without a membership has none). UX-B: this is My
  // agent's one entry (the Workforce sidebar row went), so like that row it stays with the `agents`
  // add-on off, and the page shows the add-on banner (founder's rule: never hidden).
  const myAgent =
    company !== null &&
    me.memberships.some((m) => m.company_id === company.id && m.kind === "person");
  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel className="p-0 normal-case tracking-normal">
          <ProfileCard name={name} email={me.user.email} className="px-1.5" />
        </DropdownMenuLabel>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup data-slot="your-settings">
        <DropdownMenuLabel>Your settings</DropdownMenuLabel>
        {personal.map((e) => (
          <DropdownMenuItem
            key={e.key}
            render={
              e.company && company ? (
                <Link to={e.to as never} params={{ slug: company.slug } as never} />
              ) : (
                <Link to={e.to as never} />
              )
            }
          >
            <e.icon aria-hidden="true" />
            {e.label}
          </DropdownMenuItem>
        ))}
        {company && myAgent ? (
          <DropdownMenuItem
            render={<Link to="/c/$slug/agents/me" params={{ slug: company.slug }} />}
          >
            <BotIcon aria-hidden="true" />
            My agent
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuCheckboxItem
          checked={shell.presenting}
          onCheckedChange={(checked) => shell.setPresenting(checked === true)}
        >
          Presenting
        </DropdownMenuCheckboxItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onSignOut}>
        <LogOutIcon aria-hidden="true" />
        Sign out
      </DropdownMenuItem>
    </>
  );
}
